import Konva from "konva";
import React, { useEffect, useMemo } from "react";
import { Line, Group, KonvaNodeEvents, Rect } from "react-konva";
import { useSelector } from "react-redux";

import { useConstructionExtensions } from "@/hooks";
import { ExtensionType } from "@/models/constructionExtensions";
import { RootState } from "@/reducers/types";
import { KeepOutAreaPolygon } from "@/sx-layout/components/plotmap/models";
import { useTerm } from "@/sx-layout/hooks";
import { getCurrentTimeInMinutes, timeStringToMinutes } from "@/sx-layout/utils";

const THICKNESS = 3;

type Props = Konva.LineConfig &
  KonvaNodeEvents & {
    keepOutArea: KeepOutAreaPolygon;
    mapRatio: number;
    selected?: boolean;
    setSelectedRect: (_: { x: number; y: number; width: number; height: number }) => void;
  };

export const KeepOutAreaPolygonObject = ({
  keepOutArea,
  mapRatio,
  selected,
  draggable,
  setSelectedRect,
  onClick,
  onMouseDown,
  onDragStart,
  onDragMove,
  onDragEnd,
}: Props) => {
  const {
    x,
    y,
    width,
    height,
    points,
    marginX,
    marginY,
  }: {
    x: number;
    y: number;
    width: number;
    height: number;
    points: number[];
    marginX: number;
    marginY: number;
  } = useMemo(() => {
    if (!keepOutArea?.coords)
      return {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        points: [],
        marginX: 0,
        marginY: 0,
      };
    const coords = keepOutArea.coords.map((c) => [c[0] * mapRatio, c[1] * mapRatio]);
    const xs = coords.map((p) => p[0]);
    const ys = coords.map((p) => p[1]);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      points: coords.map((p) => [p[0] - minX, p[1] - minY]).flat(),
      marginX: THICKNESS + (maxX - minX < 10 ? 5 : 0),
      marginY: THICKNESS + (maxY - minY < 10 ? 5 : 0),
    };
  }, [keepOutArea, mapRatio]);

  useEffect(() => {
    if (selected && x + y + width + height !== 0) {
      setSelectedRect({ x, y, width, height });
    }
  }, [x, y, width, height, selected]);

  const layoutDate = useSelector<RootState, Date>((state) => state.plotmap.layoutDate);
  const { isToday } = useTerm(layoutDate);
  const { extension: objectTransparentExtension } = useConstructionExtensions(ExtensionType.PLOTMAP_OBJECT_TRANSPARENT);
  const isOutsideUseHours = useMemo(() => {
    if (!isToday || !objectTransparentExtension) return false;

    const now = getCurrentTimeInMinutes();
    const start = timeStringToMinutes(keepOutArea?.use_start_hour);
    const end = timeStringToMinutes(keepOutArea?.use_end_hour);

    return now < start || end < now;
  }, [objectTransparentExtension, isToday, keepOutArea.use_start_hour, keepOutArea.use_end_hour]);

  return (
    <Group
      x={x}
      y={y}
      width={width}
      height={height}
      draggable={draggable}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
    >
      {selected && (
        <Rect
          x={-marginX}
          y={-marginY}
          width={width + marginX * 2}
          height={height + marginY * 2}
          strokeWidth={2}
          stroke="#dea321"
          dash={[5, 5]}
        />
      )}
      <Line fill={`#${keepOutArea.icon_color}`} opacity={isOutsideUseHours ? 0.1 : 0.3} points={points} closed={true} />
    </Group>
  );
};
